import React, { useCallback, useRef } from 'react';
import { useIntl } from 'react-intl';
import { usePostFileMutation } from '../../../../../../redux/api/fymAPI';
import { TypedMemo } from '../../../../../common/types';
import { getFullAPIFileURL } from '../../../../../common/getFullAPIFileURL';
import { CoverFieldActions } from '../../../../../coverEditor/entry/CoverFieldActions';
import { coverEntryMessages } from '../../../../../coverEditor/entry/coverEntryMessages';
import { coverFieldState } from '../../../../../coverEditor/entry/coverFieldState';
import { useCoverDesignOfSong } from '../../../../../coverEditor/entry/useCoverDesignOfSong';
import { useOpenCoverEditor } from '../../../../../coverEditor/entry/useOpenCoverEditor';
import { SongDraftArtwork } from '../draft/songPageDraft';
import { songPageMessages } from '../songPageMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the song page; each part uses its own slice of it */
import styles from '../SongPage.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface SongCoverFieldProps {
  accountId: number;
  catalogItemUuid: string;
  /** The cover of the song as the server has it: the field knows when the draft holds another one. */
  savedArtworkId: number | null;
  artwork: SongDraftArtwork | null;
  /** The cover of the stores of a released song with no stored file: shown while the draft has none. */
  storeCoverUrl?: string | null;
  /** A released song: its stores keep their own cover (design cover-editor a2). */
  isReleased: boolean;
  readOnly: boolean;
  onChange: (artwork: SongDraftArtwork | null) => void;
}

/**
 * The cover art of a song (design song-page b1; cover editor a1, a2): the cover and the two ways
 * to a new one, the cover editor and a file. A picked file goes up at once (`POST .../files`); its
 * id waits in the save bar until Save (`artworkFileId`). The editor saves the cover itself ("Use as
 * cover"); the leave guard of the page asks first when the save bar has changes. A released song
 * changes only its cover in Music24 (owner decision D8).
 */
const SongCoverFieldComponent: React.FC<SongCoverFieldProps> = (props) => {
  const { accountId, catalogItemUuid, savedArtworkId, artwork, storeCoverUrl, isReleased, readOnly, onChange } = props;
  const shownUrl = artwork?.url ?? storeCoverUrl ?? null;
  const intl = useIntl();
  const input = useRef<HTMLInputElement>(null);
  const [postFile, upload] = usePostFileMutation();
  const openEditor = useOpenCoverEditor({ accountId });
  const coverDesignUuid = useCoverDesignOfSong({
    accountId,
    catalogItemUuid,
    hasCover: savedArtworkId !== null,
    enabled: !readOnly,
  });
  const state = coverFieldState({
    hasCover: shownUrl !== null,
    coverDesignUuid,
    isSavedCover: artwork !== null && artwork.id === savedArtworkId,
  });

  const pick = useCallback(() => input.current?.click(), []);
  const design = useCallback(() => openEditor(catalogItemUuid), [catalogItemUuid, openEditor]);
  const remove = useCallback(() => onChange(null), [onChange]);
  const onFile = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      // The same file can be picked again after a failure.
      // eslint-disable-next-line no-param-reassign
      event.target.value = '';
      if (file === undefined) {
        return;
      }
      const formData = new FormData();
      formData.append('file', file, file.name);
      postFile({ accountId, formData })
        .unwrap()
        .then((saved) => onChange({ id: saved.id, url: getFullAPIFileURL(saved.url) }))
        .catch(console.error);
    },
    [accountId, onChange, postFile]
  );

  const label = intl.formatMessage(songPageMessages.fieldCover);
  const hints = {
    none: coverEntryMessages.hintNone,
    editor: coverEntryMessages.hintEditor,
    file: coverEntryMessages.hintFile,
  };
  let status = intl.formatMessage(hints[state]);
  if (upload.isLoading) {
    status = intl.formatMessage(songPageMessages.coverUploading);
  }

  return (
    <div className={styles.field}>
      <span className={styles.label}>{label}</span>
      <div className={styles.coverEdit}>
        {shownUrl !== null ? (
          <img className={styles.coverImage} src={shownUrl} alt="" />
        ) : (
          <span className={styles.coverImage} />
        )}
        <span className={styles.grow}>
          <span className={styles.help}>{status}</span>
        </span>
        {!readOnly && (
          <CoverFieldActions
            state={state}
            onDesign={design}
            onUpload={pick}
            isUploading={upload.isLoading}
            disabled={readOnly}
          />
        )}
        {artwork !== null && !readOnly && !isReleased && (
          <button type="button" className={styles.textButton} onClick={remove} disabled={upload.isLoading}>
            {intl.formatMessage(songPageMessages.coverRemove)}
          </button>
        )}
        <input ref={input} type="file" accept="image/*" hidden aria-label={label} onChange={onFile} />
      </div>
      {isReleased && <span className={styles.help}>{intl.formatMessage(coverEntryMessages.hintReleased)}</span>}
      {upload.isError && (
        <span className={styles.errorText} role="alert">
          {intl.formatMessage(songPageMessages.coverFailed)}
        </span>
      )}
    </div>
  );
};

export const SongCoverField = TypedMemo(SongCoverFieldComponent);
